import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import format from 'date-fns/format';
import saveAs from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';
import { Flex, Typography, Box, Card, Overlay, Loader, color, Button, Icon } from '@kistech/kis-ui';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Grid,
  Typography as MuiTypography,
  IconButton,
  TextField,
  Chip
} from '@mui/material';
import { ErrorOutline, Search, Tune, Clear } from '@mui/icons-material';
import { ActionOverlay } from 'components/ActionOverlay';
import { FilterPopUp } from './components/FilterPopUp';
import { PrinterTickets } from './components/PrinterTickets';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as deliveryLogSelectors, actions as deliveryLogActions, Transaction } from 'store/deliveryLog';
import { selectors as kioskSelectors, actions as kioskActions } from 'store/kiosks';
import { useStyles } from './styled';

const DeliveryLogPage: React.FC = () => {
  // ACTIONS AND SELECTORS
  const { getDeliveryLog, resetDeliveryLog, resetExportData, getKiosks } = useActions({
    getDeliveryLog: deliveryLogActions.getDeliveryLog,
    resetDeliveryLog: deliveryLogActions.resetDeliveryLog,
    resetExportData: deliveryLogActions.resetExportData,
    getKiosks: kioskActions.getKiosks
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const { data, pending, hasMore, totalRecords } = useSelector(deliveryLogSelectors.selectDeliveryLog);
  const exportData = useSelector(deliveryLogSelectors.selectExportData);
  const exportPending = useSelector(deliveryLogSelectors.selectExportPending);
  const kioskData = useSelector(kioskSelectors.selectKiosks);

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [searching, setSearching] = useState(false);
  const [sortColumn, setSortColumn] = useState('order_time');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [kioskIdsToFilter, setKioskIdsToFilter] = useState<string[]>([]);
  const [flowsToFilter, setFlowsToFilter] = useState<string[]>([]);
  const [deliveryTypesToFilter, setDeliveryTypesToFilter] = useState<string[]>([]);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [filterChips, setFilterChips] = useState<string[]>([]);
  const [chipClosed, setChipClosed] = useState(false);
  const [filter, setFilter] = useState(false);
  const [logRefreshedAt, setLogRefreshedAt] = useState('');

  const classes = useStyles()();

  // Showing no data to display message popup
  useEffect(() => {
    setShowErrorOverlay(data.length <= 0 && !pending);
  }, [data, pending]);

  useEffect(() => {
    if (chipClosed) {
      handleFilter();
      setChipClosed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, kioskIdsToFilter, flowsToFilter, deliveryTypesToFilter]);

  // ON UPDATE OF CLIENT
  useEffect(() => {
    // RESET DELIVERY LOG ON CLIENT CHANGE
    resetDeliveryLog();
    resetFilters();
    setPage(1);
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET DELIVERY LOGS
    if (client) {
      getKiosks({});
      getDeliveryLog({ page: 1 });
      setLogRefreshedAt(format(new Date(), 'MM/dd/yyyy hh:mm a'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // FETCH DATA FOR THE NEXT PAGE
  useEffect(() => {
    if (page > 1) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let payload: any = {
        page,
        sort: sortDirection,
        sortColumn
      };
      if (searching) {
        payload = { ...payload, orderNumber: searchTerm };
      } else if (filter) {
        const appliedFilters = getAppliedFilters();
        payload = { ...payload, ...appliedFilters };
      }

      getDeliveryLog(payload);

      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  useEffect(() => {
    if (exportData && exportData.length > 0) {
      const formattedData = exportData.map((t: Transaction) => ({
        'Order Time': format(new Date(t.orderTime), 'MM/dd/yyyy hh:mm:ss a'),
        'Order Number': t.orderNumber,
        'Kiosk Name': t.kioskName,
        'Tickets Sold': t.ticketsSold,
        Flow: t.flow,
        Type: t.deliveryType,
        'Delivery Failure': t.failedTicketCount > 0
      }));

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'Delivery Log.csv');

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  const formatOrderTime = (orderTime: string): string =>
    orderTime ? format(new Date(orderTime), 'MM/dd/yyyy hh:mm:ss a') : '';

  // RETURN ALL ACTIVE FILTERS FOR FETCH PAYLOAD
  const getAppliedFilters = () => {
    return {
      kioskIds: kioskIdsToFilter,
      startTime: selectedStartDate && moment(selectedStartDate).utc().format(),
      endTime: selectedEndDate && moment(selectedEndDate).utc().format(),
      flows: flowsToFilter,
      deliveryTypes: deliveryTypesToFilter
    };
  };

  // RESETS FILTERS
  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setKioskIdsToFilter([]);
    setFlowsToFilter([]);
    setDeliveryTypesToFilter([]);
    setFilterChips([]);
  };

  // OPEN DRAWER FOR SELECTED TRANSACTION
  const onTransactionClick = (transaction: Transaction) => {
    // only selectable if it has a failed delivery
    if (transaction.failedTicketCount > 0) {
      setSelectedTransaction(transaction);
      setShowDetailsDrawer(true);
    }
  };

  // CLOSE DRAWER
  const onDetailsDrawerClose = () => {
    setShowDetailsDrawer(false);
    setSelectedTransaction(null);
  };

  // INCREMENT PAGE TO TRIGGER NEXT FETCH
  const loadMore = () => {
    const deliveryLogTable: HTMLElement = document.getElementById('deliveryLogTable') || new HTMLElement();
    if (
      deliveryLogTable.offsetHeight < deliveryLogTable.scrollHeight &&
      deliveryLogTable.offsetHeight + deliveryLogTable.scrollTop >= deliveryLogTable.scrollHeight - 1
    ) {
      if (totalRecords >= 100 && hasMore && !pending && !isLoading) {
        setIsLoading(true);
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  // REFRESH BUTTON CLICK
  const onRefreshDeliveryLogClick = () => {
    resetDeliveryLog();
    setPage(1);
    setLogRefreshedAt(format(new Date(), 'MM/dd/yyyy hh:mm a'));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      page: 1,
      sort: sortDirection,
      sortColumn
    };
    if (searching) {
      payload = { ...payload, orderNumber: searchTerm };
    } else if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }

    getDeliveryLog(payload);
  };

  // SORT BY SELECTED HEADER AND FETCH
  const handleHeaderClick = (key: string) => {
    let newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    if (sortColumn !== key) {
      newSortDirection = 'asc';
      setSortColumn(key);
    }
    setSortDirection(newSortDirection);
    setPage(1);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      page: 1,
      sort: newSortDirection,
      sortColumn: key
    };
    if (searching) {
      payload = { ...payload, orderNumber: searchTerm };
    } else if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }

    getDeliveryLog(payload);
  };

  // SET SEARCH VALUE AND FETCH; RESET FILTERS
  const handleSearch = (searchVal: string) => {
    resetFilters();
    setSearching(true);
    setFilter(false);
    setPage(1);

    getDeliveryLog({
      page: 1,
      sort: sortDirection,
      sortColumn,
      orderNumber: searchVal.trim()
    });
  };

  // APPLY FILTERS AND FETCH; RESET SEARCH
  const handleFilter = () => {
    setFilter(true);
    setSearchTerm('');
    setSearching(false);
    setPage(1);

    const appliedFilters = getAppliedFilters();
    getDeliveryLog({
      page: 1,
      sort: sortDirection,
      sortColumn,
      ...appliedFilters
    });
  };

  // RESET SEARCH AND FETCH
  const handleClear = () => {
    setSearchTerm('');
    setSearching(false);
    setPage(1);

    getDeliveryLog({
      page: 1,
      sort: sortDirection,
      sortColumn
    });
  };

  // REMOVE AN ACTIVE FILTER
  const handleChipClose = (val: string) => {
    if (val === 'Kiosk Name') {
      setKioskIdsToFilter([]);
    } else if (val === 'Flow') {
      setFlowsToFilter([]);
    } else if (val === 'Delivery Type') {
      setDeliveryTypesToFilter([]);
    } else if (val === 'Start Date Time') {
      setSelectedStartDate(null);
    } else if (val === 'End Date Time') {
      setSelectedEndDate(null);
    }

    const newChips = filterChips && filterChips.filter((chip: string) => chip !== val);
    setFilterChips(newChips);
  };

  // EXPORT TO CSV BUTTON CLICK
  const onExportButtonClick = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let payload: any = {
      page: 1,
      sort: sortDirection,
      sortColumn,
      exportData: true
    };

    if (searching) {
      payload = { ...payload, orderNumber: searchTerm };
    } else if (filter) {
      const appliedFilters = getAppliedFilters();
      payload = { ...payload, ...appliedFilters };
    }

    getDeliveryLog(payload);
  };

  const SortArrow = () => {
    return (
      <Box style={{ display: 'inline-block', cursor: 'pointer' }}>
        <Icon
          name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'}
          color="#2A2A2A"
          size={15}
          style={{ marginLeft: '5px' }}
        />
      </Box>
    );
  };

  const isSearchValueEmpty = searchTerm.length === 0;

  // IF NO CLIENT GO TO CLIENTS PAGE
  if (!client) return <Redirect to="/clients" />;

  return (
    <>
      {(pending || isLoading) && data.length <= 0 && (
        <Overlay>
          <Loader color={color.LIGHT_GRAY} />
        </Overlay>
      )}
      {showErrorOverlay && !pending && (
        <ActionOverlay
          title={
            <MuiBox className={classes.deliverylogNoDataBox}>
              <MuiTypography className={classes.deliverylogNoDataHeader}>There is no data to display</MuiTypography>
            </MuiBox>
          }
          confirmText="Ok"
          onConfirm={() => setShowErrorOverlay(false)}
          height={340}
        />
      )}
      {/* HEADER ROW */}
      <Grid className={classes.deliverylogHeading}>
        <Box flexOverride="none">
          <MuiTypography className={classes.deliverylogText}>Delivery Log</MuiTypography>
        </Box>
        <Grid className={classes.deliveryRefreshEventButton}>
          <Flex direction="column">
            <Button
              size="small"
              onClick={onRefreshDeliveryLogClick}
              isLoading={!searching && !filter && pending && data.length <= 0}
              data-testid="refresh-delivery-log-button"
            >
              Refresh All Data
            </Button>
            <Typography tag="small" align="center" style={{ margin: '5px 0 0' }}>
              Last Refreshed: {logRefreshedAt}
            </Typography>
          </Flex>
        </Grid>
      </Grid>
      <Grid
        className={classes.deliverylogOptions}
        style={{ paddingBottom: filterChips && filterChips.length === 0 ? '30px' : '0px' }}
      >
        <div className={classes.root}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            placeholder="Search Order Number"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value.length === 0) handleClear();
            }}
            InputProps={{
              classes: {
                root: classes.inputRoot
              },
              endAdornment: searchTerm.length > 0 && (
                <IconButton onClick={handleClear}>
                  <Clear />
                </IconButton>
              )
            }}
          />
          <IconButton
            disabled={isSearchValueEmpty}
            onClick={() => handleSearch(searchTerm)}
            style={{
              backgroundColor: isSearchValueEmpty ? '' : '#F46D2E',
              color: isSearchValueEmpty ? 'grey' : 'white'
            }}
          >
            <Search />
          </IconButton>
        </div>
        <Grid style={{ marginLeft: '40px' }}>
          <Flex direction="column">
            <Button size="small" onClick={() => setShowFiltersDrawer(true)} data-testid="filter-button">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tune />
                <Typography style={{ margin: '0px 10px 0px 10px', fontSize: '16px' }}>Filters</Typography>
              </div>
            </Button>
          </Flex>
        </Grid>
        <Grid style={{ marginLeft: '40px', display: 'flex', width: '100%', justifyContent: 'end' }}>
          <Flex direction="column">
            <Button
              onClick={() => onExportButtonClick()}
              isLoading={exportPending}
              style={{ width: '200px' }}
              isDisabled={data.length <= 0}
              data-testid="filter-button"
            >
              <div style={{ alignItems: 'center' }}>
                <Typography style={{ textAlign: 'center', color: 'white' }}>Export to .csv</Typography>
              </div>
            </Button>
          </Flex>
        </Grid>
      </Grid>
      {filterChips && filterChips.length > 0 && (
        <Grid className={classes.filterOptions}>
          {filterChips.map((chip: string, index: number) => (
            <Chip
              key={`${index}-${chip}`}
              style={{
                backgroundColor: '#F46D2E',
                color: 'white',
                fontWeight: 900,
                marginRight: '8px'
              }}
              className={classes.buttonStyl}
              label={chip}
              onDelete={() => {
                handleChipClose(chip);
                setChipClosed(true);
              }}
              variant="outlined"
            />
          ))}
        </Grid>
      )}
      {/* TABLE OF USERS */}
      <MuiBox className={classes.deliverylogOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <TableContainer className={classes.deliveryLogTableContainer} onScroll={loadMore} id="deliveryLogTable">
              <MuiTable stickyHeader style={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.deliverylogHeaders}
                      style={{ fontWeight: 600, width: '240px' }}
                      onClick={() => handleHeaderClick('order_time')}
                    >
                      Order Time
                      {sortColumn === 'order_time' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.deliverylogHeadersOrder}
                      style={{ fontWeight: 600, width: '220px' }}
                      onClick={() => handleHeaderClick('order_number')}
                    >
                      Order Number
                      {sortColumn === 'order_number' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.deliverylogHeaders}
                      style={{ fontWeight: 600, width: '250px' }}
                      onClick={() => handleHeaderClick('kiosk_name')}
                    >
                      Kiosk Name
                      {sortColumn === 'kiosk_name' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.deliverylogHeaders}
                      style={{ fontWeight: 600, width: '110px' }}
                      onClick={() => handleHeaderClick('ticket_count')}
                    >
                      Tickets
                      {sortColumn === 'ticket_count' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.deliverylogHeaders}
                      style={{ fontWeight: 600, width: '120px' }}
                      onClick={() => handleHeaderClick('flow')}
                    >
                      Flow
                      {sortColumn === 'flow' && <SortArrow />}
                    </TableCell>
                    <TableCell
                      className={classes.deliverylogHeaders}
                      style={{ fontWeight: 600, width: '110px' }}
                      onClick={() => handleHeaderClick('delivery_type')}
                    >
                      Type
                      {sortColumn === 'delivery_type' && <SortArrow />}
                    </TableCell>
                    <TableCell className={classes.deliverylogHeaders} style={{ fontWeight: 600, width: '100px' }} />
                  </TableRow>
                </TableHead>
                <TableBody className={classes.deliverylogTableBody}>
                  {data.map((deliverylogObj: Transaction) => {
                    return (
                      <TableRow
                        key={deliverylogObj.id}
                        onClick={() => onTransactionClick(deliverylogObj)}
                        className={classes.deliverylogTableRows}
                        selected={selectedTransaction ? selectedTransaction.id === deliverylogObj.id : undefined}
                        classes={{ selected: classes.selected }}
                      >
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16} weight={600}>
                            {formatOrderTime(deliverylogObj.orderTime)}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16}>{deliverylogObj.orderNumber}</Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16}>{deliverylogObj.kioskName}</Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16}>{deliverylogObj.ticketsSold}</Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16}>{deliverylogObj.flow}</Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Typography size={16}>{deliverylogObj.deliveryType}</Typography>
                        </TableCell>
                        <TableCell className={classes.deliverylogTableRows}>
                          <Flex>
                            {deliverylogObj.failedTicketCount > 0 ? (
                              <Typography size={16} weight={600} color="#c40b0f">
                                <ErrorOutline />
                              </Typography>
                            ) : (
                              ''
                            )}
                          </Flex>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Box>
        </Card>
      </MuiBox>

      <Grid className={classes.footer}>
        <p className={classes.total}>Orders: {totalRecords}</p>
      </Grid>

      <PrinterTickets onClose={onDetailsDrawerClose} isOpen={showDetailsDrawer} transaction={selectedTransaction} />
      <FilterPopUp
        onClose={() => setShowFiltersDrawer(false)}
        isOpen={showFiltersDrawer}
        kioskIdsToFilter={kioskIdsToFilter}
        setKioskIdsToFilter={setKioskIdsToFilter}
        flowsToFilter={flowsToFilter}
        setFlowsToFilter={setFlowsToFilter}
        deliveryTypesToFilter={deliveryTypesToFilter}
        setDeliveryTypesToFilter={setDeliveryTypesToFilter}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        filterChips={filterChips}
        setFilterChips={setFilterChips}
        kioskList={kioskData}
        handleFilter={handleFilter}
      />
    </>
  );
};

export default DeliveryLogPage;
